import { useEffect, useState } from "react";
import { Warning, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/constants";
import { shareWhatsApp } from "@/lib/whatsapp";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const BUCKETS = [
  { key: "current", label: "Not yet due", tone: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "b0_30", label: "0–30 days overdue", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "b31_60", label: "31–60 days overdue", tone: "bg-orange-100 text-orange-800 border-orange-200" },
  { key: "b60_plus", label: "60+ days overdue", tone: "bg-rose-100 text-rose-700 border-rose-200" },
];

export default function Outstanding() {
  const [data, setData] = useState(null);

  const load = () => api.get("/reports/aging").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const chase = async (inv) => {
    try { await api.post(`/invoices/${inv.id}/send-email`); toast.success("Reminder email sent with a pay link"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (data === false) return <EmptyState icon={Warning}>Could not load the aging report.</EmptyState>;
  if (!data) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Building aging report…</div>;

  const totalOutstanding = BUCKETS.reduce((s, b) => s + data[b.key].amount, 0);
  const overdueAmount = totalOutstanding - data.current.amount;

  return (
    <div className="space-y-6" data-testid="outstanding-page">
      <PageHeader overline="Invoices" title="Outstanding / Overdue" subtitle="Collections view — chase receivables by how long they've been sitting" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total outstanding" value={inr(totalOutstanding)} testid="stat-total-out" />
        <Stat label="Overdue" value={inr(overdueAmount)} tone="text-rose-600" testid="stat-overdue-amt" />
        <Stat label="60+ days" value={inr(data.b60_plus.amount)} tone="text-rose-700" sub={`${data.b60_plus.count} invoices`} testid="stat-60plus" />
        <Stat label="Not yet due" value={inr(data.current.amount)} tone="text-emerald-600" sub={`${data.current.count} invoices`} testid="stat-not-due" />
      </div>

      <div className="flex h-3 rounded-full overflow-hidden border border-border bg-muted" data-testid="aging-bar">
        {BUCKETS.map((b) => {
          const pct = totalOutstanding ? (data[b.key].amount / totalOutstanding) * 100 : 0;
          const fill = { current: "#94a3b8", b0_30: "#f59e0b", b31_60: "#ea580c", b60_plus: "#e11d48" }[b.key];
          return pct > 0 ? <div key={b.key} style={{ width: `${pct}%`, backgroundColor: fill }} title={`${b.label}: ${inr(data[b.key].amount)}`} /> : null;
        })}
      </div>

      {totalOutstanding === 0 && <EmptyState icon={Warning}>Every invoice is settled. Nothing to chase right now.</EmptyState>}

      {BUCKETS.map((b) => data[b.key].count > 0 && (
        <Card key={b.key} className="border-border overflow-hidden" data-testid={`bucket-${b.key}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 bg-muted border-b border-border">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase tracking-wider ${b.tone}`}>{b.label}</span>
              <span className="text-xs text-muted-foreground">{data[b.key].count} invoices</span>
            </div>
            <div className="font-display font-bold text-primary tabular">{inr(data[b.key].amount)}</div>
          </div>
          <div className="divide-y divide-border">
            {data[b.key].items.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`aging-inv-${i.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-primary truncate">{i.bill_to_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {i.invoice_number} · due {(i.due_date || "—").slice(0, 10)}{i.days_overdue ? ` · ${i.days_overdue}d overdue` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="font-display font-bold text-primary tabular">{inr(i.balance_due)}</div>
                  <Button size="icon" variant="outline" className="h-8 w-8" title="Email reminder with pay link" onClick={() => chase(i)} data-testid={`chase-email-${i.id}`}>
                    <EnvelopeSimple size={15} />
                  </Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600" title="Nudge on WhatsApp"
                    onClick={() => shareWhatsApp("", `*Payment reminder* from Aakriti Associates\nInvoice: ${i.invoice_number}\nAmount due: ₹${Number(i.balance_due).toLocaleString("en-IN")}\nDue date: ${(i.due_date || "").slice(0, 10)}${i.payment_link_url ? `\nPay online: ${i.payment_link_url}` : ""}`)}
                    data-testid={`chase-wa-${i.id}`}>
                    <WhatsappLogo size={15} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
